import { LinearProgress } from '@mui/material';
import { Metadata } from '@redwoodjs/web';
import { RiBuilding4Fill, RiLeafFill, RiSettings3Line } from '@remixicon/react';
import { useState } from 'react';
import ExportGridButton from 'src/components/atoms/ExportGridButton';
import Flex from 'src/components/atoms/Flex';
import { PageBody } from 'src/components/containers/PageBody';
import { PageHeader, PageType } from 'src/components/containers/PageHeader';
import { AgTab, AgTabs } from 'src/components/ui/AgTabs';
import { preloadRouteQuery } from 'src/routing/routePreload';
import type { AppRouterContext } from 'src/routing/routerAccess';
import { useSettingsInventorySearch } from 'src/routing/routeSearch';
import CommoditiesGrid from './commodities/CommoditiesGrid';
import { preloadCatalogRoot } from './commodities/catalogRootSeed';
import { InventorySettingsTabs } from './inventorySettings.constants';
import InventoryPreferencesTab from './preferences/InventoryPreferencesTab';
import WarehousesGrid, { WAREHOUSE_CARDS_FIRST_PAGE, WAREHOUSE_CARDS_PAGE } from './warehouses/WarehousesGrid';

export { InventorySettingsTabs };

/** The shown tab's first read: the catalog's first commodity window, or the warehouse list. */
export function preloadInventorySettingsTab(context: AppRouterContext, tab: string | undefined) {
  if (tab === undefined || tab === InventorySettingsTabs.COMMODITIES) {
    return { catalogRootRef: preloadCatalogRoot(context) };
  }
  if (tab === InventorySettingsTabs.WAREHOUSES) {
    return {
      warehousesRef: preloadRouteQuery(context, WAREHOUSE_CARDS_PAGE, WAREHOUSE_CARDS_FIRST_PAGE, {
        fetchPolicy: 'cache-and-network',
      }),
    };
  }
  return {};
}

export default function InventorySettingsPage() {
  const [downloading, setDownloading] = useState(false);
  const { tab } = useSettingsInventorySearch();

  return (
    <>
      <Metadata title="Inventory Settings" />
      <PageHeader pageType={PageType.InventorySettings}>
        <Flex center sx={{ gap: 1, position: 'relative', width: '100%' }}>
          <Flex flexGrow={1} />
          {(!tab || tab === InventorySettingsTabs.COMMODITIES) && <ExportGridButton />}
          {downloading && (
            <LinearProgress
              sx={{
                position: 'absolute',
                transform: 'translate(0, -100%)',
                width: '100%',
                borderRadius: '0 0 4px 4px',
              }}
            />
          )}
        </Flex>
      </PageHeader>
      <PageBody>
        <AgTabs queryParam="tab">
          <AgTab id={InventorySettingsTabs.COMMODITIES} label="Commodities & Products" icon={<RiLeafFill size={20} />}>
            <CommoditiesGrid setDownloading={setDownloading} />
          </AgTab>
          <AgTab id={InventorySettingsTabs.WAREHOUSES} label="Warehouses" icon={<RiBuilding4Fill size={20} />}>
            <WarehousesGrid setDownloading={setDownloading} />
          </AgTab>
          <AgTab id={InventorySettingsTabs.PREFERENCES} label="Preferences" icon={<RiSettings3Line size={20} />}>
            <InventoryPreferencesTab setDownloading={setDownloading} />
          </AgTab>
        </AgTabs>
      </PageBody>
    </>
  );
}
